'use client';

import * as ENGINE from '@gnsx/genesys.js';
import { destroy, detach, getParent, getParentOfType, hasParent, isAlive, isStateTreeNode, t } from 'mobx-state-tree';

import { addComponentInternal, duplicateComponentInternal, removeComponentInternal } from '../../core/actions/objectActions';

import { withoutRemovedNodeIds } from './objectTreeStateHelpers';

import type { IEditableObject } from './editableObject';
import type { UniversalAccessor } from '@/editor/core/universalAccessor';
import type { TreeInstance } from '@headless-tree/core';
import type { IAnyModelType, Instance } from 'mobx-state-tree';
import type * as THREE from 'three';

import { objectHelpers } from '@/editor/core';
import {
  canRecordPrefabEditorUndo,
  recordActorSnapshotAction,
} from '@/editor/core/undoRedo';
import { createAccessorFromComponent, createAccessorFromNode, createAccessorFromNodeWithProperty } from '@/editor/core/universalAccessor';
import { editorStore } from '@/editor/store/editorStore';
import { castAs } from '@/lib/utils';


const DEBUG_LOG = {
  expandedNodesIds: false,
} as const;

function isEditableObjectParent(value: unknown): value is IEditableObject {
  return !!value && typeof value === 'object' && 'hierarchy' in value && 'asActor' in value;
}

/**
 * ObjectTreeNode - represents a node (Actor/SceneComponent) in the object tree hierarchy
 */
export const ObjectTreeNode = t
  .model('ObjectTreeNode', {
    id: t.identifier,
    name: t.string,
    children: t.array(t.late((): IAnyModelType => ObjectTreeNode)),
    isExpanded: t.optional(t.boolean, true),
    isSelected: t.optional(t.boolean, false),
    isVisible: t.optional(t.boolean, true),
  })
  .volatile(() => ({
    target: undefined as ENGINE.Actor | ENGINE.SceneComponent | undefined,
    sceneObject: undefined as THREE.Object3D | undefined,
    isPrefabInstance: false,
    isPrefabAsset: false,
  }))
  .views(self => ({
    get displayName() { return self.name; },
    get className() { return objectHelpers.getClassName(self.target, false); },
    get shortClassName() { return objectHelpers.getClassName(self.target, true); },
    get asActor() { return castAs(self.target, ENGINE.Actor); },
    get asComponent() { return castAs(self.target, ENGINE.SceneComponent); },
    get editableObjectParent(): IEditableObject | undefined {
      let current: unknown = self;
      while (isStateTreeNode(current) && hasParent(current)) {
        const parent = getParent(current);
        if (isEditableObjectParent(parent)) return parent;
        current = parent;
      }
      return undefined;
    },
    get isRootComponent() { return this.asComponent?.isRootComponent(); },
    get isPrefabEditLocked(): boolean {
      return editorStore.isPrefabMode && (this.editableObjectParent?.isEngineAsset ?? false);
    },
    get isRenamable() { return !this.isTransient && !self.isPrefabInstance && !this.isPrefabEditLocked; },
    get isTransient() { return self.target?.isTransient(); },
    get isRemovable() { return !this.asActor && !self.isPrefabInstance && !this.isRootComponent && !this.isPrefabEditLocked; },
    getActor(): ENGINE.Actor | undefined { return this.editableObjectParent?.asActor; }
  }))
  /* Node Actions */
  .actions(self => ({
    init(target: ENGINE.Actor | ENGINE.SceneComponent) {
      self.target = target;
      self.name = target.name;
      self.sceneObject = target instanceof ENGINE.SceneComponent ? target : target instanceof ENGINE.Actor ? target.rootComponent : undefined;
      self.isVisible = true;

      const actor = self.getActor();
      self.isPrefabInstance = actor ? objectHelpers.isPrefabInstance(actor) : false;
      self.isPrefabAsset = actor ? objectHelpers.isPrefabAsset(actor) : false;
    },
    setSelected(selected: boolean) {
      self.isSelected = selected;
    },
    setExpanded(expanded: boolean) {
      self.isExpanded = expanded;
    },
    setName(newName: string) {
      self.name = newName;
    },
    setVisibility(visible: boolean) {
      self.isVisible = visible;
    },
    toggleVisibility() {
      this.setVisibility(!self.isVisible);
    }
  }))
  /* Component Actions */
  .actions(self => ({
    /**
     * Add a component to the object hierarchy tree.
     * @param className - The class name of the component to add.
     * @param selectNewNode - Whether to select the new node.
     * @returns The added component node or undefined if failed.
     */
    addComponent(className: string, selectNewNode: boolean = true) {
      if (!canRecordPrefabEditorUndo() && editorStore.isPrefabMode) {
        return;
      }

      const accessor = createAccessorFromNode(self as IObjectTreeNode);
      if (accessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for node');
        return;
      }

      const parentAccessor = accessor.resolveOwningActorAccessor();
      if (parentAccessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for parent actor');
        return;
      }

      const preCalculatedUuid = ENGINE.shortUUID();

      recordActorSnapshotAction(
        'Add component',
        parentAccessor,
        () => {
          addComponentInternal(accessor, className, selectNewNode, preCalculatedUuid);
        },
      );
    },

    /**
     * Duplicate a component in the object hierarchy tree with undo/redo support.
     * @param component - The component to duplicate.
     * @param selectNewNode - Whether to select the new node.
     * @returns The added component node or undefined if failed.
     */
    duplicateComponent(component: ENGINE.SceneComponent, selectNewNode: boolean = true) {
      if (!canRecordPrefabEditorUndo() && editorStore.isPrefabMode) {
        return;
      }

      const componentAccessor = createAccessorFromComponent(component);
      if (componentAccessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for component');
        return;
      }

      const parentAccessor = componentAccessor.resolveOwningActorAccessor();
      if (parentAccessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for parent actor');
        return;
      }

      const preCalculatedUuid = ENGINE.shortUUID();

      let createdNode: IObjectTreeNode | undefined;

      recordActorSnapshotAction(
        'Duplicate component',
        parentAccessor,
        () => {
          const result = duplicateComponentInternal(componentAccessor, selectNewNode, preCalculatedUuid);
          createdNode = result.createdNode;
        },
      );
      return createdNode;
    },

    removeComponent() {
      if (!self.isRemovable) return;

      const accessor = createAccessorFromNode(self as IObjectTreeNode);
      if (accessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for component');
        return;
      }

      const parentAccessor = accessor.resolveOwningActorAccessor();
      if (parentAccessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for parent actor');
        return;
      }

      recordActorSnapshotAction(
        'Remove component',
        parentAccessor,
        () => {
          removeComponentInternal(accessor);
        },
      );
    },
    renameComponent(name: string) {
      if (!canRecordPrefabEditorUndo() && editorStore.isPrefabMode) {
        return;
      }

      const oldName = self.name;
      const newName = name;

      // Create a universal accessor for this component
      const accessor = createAccessorFromNodeWithProperty(self as IObjectTreeNode, 'name');
      if (accessor.isInvalid) {
        console.warn('Cannot create undo action: failed to create accessor for component');
        return;
      }

      function makeRenameAction(acc: UniversalAccessor, to: string) {
        return () => {
          acc.setValue(to);
          if (self && isAlive(self)) {
            self.setName(to); // Keep MST node in sync so component tree row re-renders
          }
          editorStore.scene.updateInspector();
        };
      }

      const undoManager = editorStore.undoRedoManager;

      void undoManager.recordAction(
        'Rename component',
        makeRenameAction(accessor, newName),
        makeRenameAction(accessor, oldName),
      );

      console.log('renameComponent', { oldName, newName, actorName: self.name, uuid: self.id });
    }
  }));

export interface IObjectTreeNode extends Instance<typeof ObjectTreeNode> { }

/**
 * ObjectTree
 */
export const ObjectTree = t
  .model('ObjectTree', {
    nodes: t.array(ObjectTreeNode),
    nodeMap: t.map(t.safeReference(ObjectTreeNode, { acceptsUndefined: false })),
    selectedNodes: t.array(t.safeReference(ObjectTreeNode, { acceptsUndefined: false })),
    expandedNodes: t.map(t.safeReference(ObjectTreeNode, { acceptsUndefined: false })),
    /** Bumped after hierarchy changes; the MobX observer in ComponentTreeView
     *  subscribes to this so headless-tree can schedule a rebuild during render. */
    childrenOrderVersion: t.optional(t.number, 0),
  })
  .volatile(() => ({
    /** Cached tree instance for accessing tree methods and state */
    treeView: undefined as TreeInstance<IObjectTreeNode> | undefined,
  }))
  .views(self => ({
    get selectedLiveNodes() {
      return self.selectedNodes.filter(node => {
        if (!node) return false;
        try {
          return self.nodeMap.has(node.id);
        } catch {
          return false;
        }
      });
    },
    get selectedNode() {
      return this.selectedLiveNodes.length > 0 ? this.selectedLiveNodes[0] : undefined;
    },
    get selectedNodeId() {
      return this.selectedLiveNodes.length > 0 ? this.selectedLiveNodes[0]?.id : undefined;
    },
    get selectedNodeIds() {
      return this.selectedLiveNodes.map(node => node.id);
    },
    get selectedActors() {
      return this.selectedLiveNodes.map(node => node.asActor).filter(Boolean) as ENGINE.Actor[];
    },
    get selectedSceneComponents() {
      return this.selectedLiveNodes.map(node => node.asComponent).filter(Boolean) as ENGINE.SceneComponent[];
    },
    get selectedSceneObjects() {
      return this.selectedLiveNodes.map(node => node.sceneObject).filter(Boolean) as THREE.Object3D[];
    },
    get expandedNodeIds() {
      return Array.from(self.expandedNodes.keys()).filter(id => self.nodeMap.has(id));
    },
    get rootNode() {
      return self.nodes[0]!;
    },
    getNodeById(id: string): IObjectTreeNode | undefined {
      try {
        return self.nodeMap.get(id);
      } catch (err) {
        console.warn('[ObjectTree] getNodeById failed to resolve id; treating as missing', { id, error: err });
        return undefined;
      }
    },
  }))
  .actions(self => ({
    _addToNodeMap(node: IObjectTreeNode) {
      if (process.env.NODE_ENV !== 'production') {
        try {
          const existing = self.nodeMap.get(node.id);
          if (existing && existing !== node) {
            console.warn('[ObjectTree] duplicate ObjectTreeNode id detected', { id: node.id, name: node.name });
          }
        } catch {
          // Ambiguous or invalid ref; primary fix is actor-scoped buildObjectTree traversal
        }
      }
      self.nodeMap.put(node);
      node.children.forEach(child => this._addToNodeMap(child));
    },
    _removeFromNodeMap(node: IObjectTreeNode) {
      node.children.forEach(child => this._removeFromNodeMap(child));
      self.nodeMap.delete(node.id);
    },
    reset() {
      self.nodes.clear();
      self.selectedNodes.clear();
      self.expandedNodes.clear();
      self.nodeMap.clear();
      self.childrenOrderVersion = 0;
    },
    setTreeView(tree: TreeInstance<IObjectTreeNode> | undefined) {
      self.treeView = tree;
    },
    scrollToSelected() {
      const selectedNode = self.selectedNode;
      if (!selectedNode || !self.treeView) return;

      const item = self.treeView.getItemInstance(selectedNode.id);
      if (item) {
        item.scrollTo();
      }
    },
    /**
     * Add a node to the object hierarchy tree. If the node has a parent, it will be detached first.
     * @param newNode - The node to add.
     * @param parentIdOrNode - The parent node or parent ID.
     * @returns The added node.
     */
    addNode(newNode: IObjectTreeNode, parentIdOrNode: string | IObjectTreeNode | undefined) {
      this._removeFromNodeMap(newNode);
      if (hasParent(newNode)) {
        detach(newNode);
      }
      const parent = (typeof parentIdOrNode === 'string') ? self.getNodeById(parentIdOrNode) : parentIdOrNode;
      const children = parent?.children ?? self.nodes;
      children.push(newNode);
      this._addToNodeMap(newNode);

      if (newNode.isExpanded) {
        self.expandedNodes.put(newNode);
      }
      return newNode;
    },
    /** Increment version counter so the ComponentTreeView observer re-renders. */
    bumpVersion() {
      self.childrenOrderVersion += 1;
    },
    removeNode(id: string, removeChildren: boolean = true) {
      const node = self.getNodeById(id);
      if (!node) return;

      const removedNodeIds = new Set<string>();
      const collectRemovedNodeIds = (currentNode: IObjectTreeNode) => {
        removedNodeIds.add(currentNode.id);
        currentNode.children.forEach(child => collectRemovedNodeIds(child));
      };
      collectRemovedNodeIds(node);

      this._removeFromNodeMap(node);

      // Remove from expandedNodes and selectedNodes (including all descendants).
      removedNodeIds.forEach(removedId => self.expandedNodes.delete(removedId));
      const selectedNodeIdsToKeep = new Set(
        withoutRemovedNodeIds(
          self.selectedNodes.map(node => node.id),
          removedNodeIds,
        ),
      );
      self.selectedNodes.replace(
        self.selectedNodes.filter(selectedNode => selectedNodeIdsToKeep.has(selectedNode.id))
      );

      // If not removing children, move them to the parent node
      if (!removeChildren) {
        const parent = getParentOfType(node, ObjectTreeNode);
        if (parent) {
          const children = [...node.children];
          children.forEach(child => {
            this.addNode(detach(child), parent.id);
          });
        }
      }
      // Detach the node from its parent before destroying
      if (hasParent(node)) {
        detach(node);
      }
      // Destroy the node
      destroy(node);
      this.bumpVersion();
    },
    selectNode(id: string | null, multiSelect: boolean = false) {
      if (id == null) {
        self.selectedNodes.forEach(node => node.setSelected(false));
        self.selectedNodes.clear();
        return;
      }

      const node = self.getNodeById(id);
      if (!node) return;

      const existingIndex = self.selectedNodes.findIndex(n => n.id === id);

      if (multiSelect && existingIndex !== -1) {
        node.setSelected(false);
        self.selectedNodes.splice(existingIndex, 1);
        return;
      }

      if (!multiSelect) {
        self.selectedNodes.forEach(n => n.setSelected(false));
        self.selectedNodes.clear();
      }

      if (existingIndex === -1) {
        node.setSelected(true);
        self.selectedNodes.push(node);
      }
    },
    selectNodes(nodes: IObjectTreeNode[]) {
      this.setSelectedNodeIds(nodes.map(node => node.id));
    },
    setSelectedNodeIds(updater: string[] | ((ids: string[]) => string[])) {
      const ids = Array.isArray(updater) ? updater : updater([]);
      self.selectedNodes.forEach(node => node.setSelected(false));
      const nodes = ids.map(id => self.getNodeById(id)).filter(Boolean) as IObjectTreeNode[];
      nodes.forEach(node => node.setSelected(true));
      self.selectedNodes.replace(nodes);
    },
    setExpandedNodesIds(updater: string[] | ((ids: string[]) => string[])) {
      const currentIds = self.expandedNodeIds;
      const ids = Array.isArray(updater) ? updater : updater(currentIds);

      DEBUG_LOG.expandedNodesIds && console.log('🔄 setExpandedNodesIds called', {
        currentIds,
        newIds: ids,
        nodeMapSize: self.nodeMap.size
      });

      // Instead of clearing all nodes, only update the ones that changed
      const currentSet = new Set(currentIds);
      const newSet = new Set(ids);

      // Find nodes that should be collapsed (were expanded but not in new list)
      const toCollapse = currentIds.filter(id => !newSet.has(id));
      // Find nodes that should be expanded (are in new list but weren't expanded)
      const toExpand = ids.filter(id => !currentSet.has(id));

      DEBUG_LOG.expandedNodesIds && console.log('🔄 Changes:', { toCollapse, toExpand });

      // Collapse nodes that should be collapsed
      toCollapse.forEach(id => {
        const node = self.getNodeById(id);
        if (node) {
          node.setExpanded(false);
          self.expandedNodes.delete(id);
        }
      });

      // Expand nodes that should be expanded
      toExpand.forEach(id => {
        const node = self.getNodeById(id);
        if (node) {
          node.setExpanded(true);
          self.expandedNodes.put(node);
        } else {
          console.warn('⚠️ Node not found for expansion:', id);
        }
      });

      DEBUG_LOG.expandedNodesIds && console.log('✅ setExpandedNodesIds completed', {
        expandedNodesCount: self.expandedNodes.size,
        expandedIds: Array.from(self.expandedNodes.keys())
      });
    },
  }));
export interface IObjectTree extends Instance<typeof ObjectTree> { }